import { PermissionsService } from '@services/auth/permissions.service';
import { MenuService } from '@services/menu/menu.service';
import { Component, Input, OnDestroy, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { EquationBar } from '@models/equation-bar';
import { WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { Subscription } from 'rxjs';
import { EquationBuilderComponent } from '../equation-builder/equation-builder.component';

@Component({
  selector: 'app-equation-bar',
  templateUrl: './equation-bar.component.html',
  styleUrls: ['./equation-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EquationBarComponent implements OnDestroy {
  @Input() equationBar: EquationBar = null;
  @ViewChild('container', { static: false, read: ViewContainerRef })
  public containerRef: ViewContainerRef;
  private windowRef: WindowRef = null;
  private subscription: Subscription = null;
  public isOpen = false;
  public userRoleCanEdit: boolean;

  get isPeriodCellSelected() {
    return this.equationBar ? this.equationBar.activeCell.col === 0 : false;
  }

  constructor(
    private windowService: WindowService,
    private menu: MenuService,
    private permissions: PermissionsService
  ) {
    // Only create a new EquationBar if none is provided via @Input()
    if (!this.equationBar) {
      this.equationBar = new EquationBar(null, { row: null, col: null });
    }

    switch (this.menu.selectedMenuItem.label) {
      case 'Exogenous Equations':
        this.userRoleCanEdit = this.permissions.getPermission('ModelDataExogenousLinesEquations') === 'W';
        break;
      case 'Liquidity Source Equations':
        this.userRoleCanEdit = this.permissions.getPermission('ModelDataLiquiditySourcesEquations') === 'W';
        break;
      case 'Auxiliary Equations':
        this.userRoleCanEdit = this.permissions.getPermission('ModelDataAuxiliaryAccountsEquations') === 'W';
        break;
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  equationContentChanged(event) {
    this.equationBar.contentChanged(event);
  }

  openEquationBuilder(event) {
    const doBeforeClose = (closeEvent) => {
      closeEvent.preventDefault();
      closeEvent.stopPropagation();
      closeEvent.isDefaultPrevented = true;
      closeEvent.isPropagationStopped = true;
      if (!confirm('Unsaved changes! Do you want to leave and discard your changes?')) {
        // Leave
        this.windowRef.close();
      }
    };

    const kendoOptions = {
      appendTo: this.containerRef,
      title: `Equation Builder`,
      content: EquationBuilderComponent,
      top: 200,
      width: 800,
      height: 650,
      minWidth: 600,
      minHeight: 630,
      unique: true,
      equation: this.equationBar,
      deactivate: doBeforeClose,
      close: doBeforeClose,
      onclose: doBeforeClose,
    };

    if (!this.isOpen) {
      this.isOpen = true;
      this.windowRef = this.windowService.open(kendoOptions);
      this.windowRef.content.location.nativeElement.focus();

      const equationInfo = this.windowRef.content.instance;
      equationInfo.equation = this.equationBar;

      // Listen for Kendo window events and trigger ACE resize
      if (this.windowRef.window && this.windowRef.window.instance) {
        if (this.windowRef.window.instance.stateChange) {
          this.windowRef.window.instance.stateChange.subscribe(() => {
            equationInfo.triggerAceResize();
          });
        }
        if (this.windowRef.window.instance.resizeEnd) {
          this.windowRef.window.instance.resizeEnd.subscribe(() => {
            equationInfo.triggerAceResize();
          });
        }
      }

      this.subscription = this.windowRef.result.subscribe((result: any) => {
        if (result.equationSaved) {
          this.equationBar.content = result.equation;
          this.equationBar.content$.next(result.equation);
        }
        this.isOpen = false;
        this.subscription.unsubscribe();
        this.equationBar.focusSpread();
      });
    }
  }
}
